import React from "react";
import { Box, Typography, Stack, Skeleton } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { motion, AnimatePresence } from "framer-motion";
import BoardCard from "./BoardCard";
import { LANE_CONFIG } from "./boardConfig";

function LaneEmptyState({ isDark }) {
  return (
    <Box
      sx={{
        mt: 2,
        py: 4,
        px: 2,
        textAlign: "center",
        borderRadius: "14px",
        border: `1px dashed ${isDark ? "rgba(255,255,255,0.10)" : "rgba(15,23,42,0.10)"}`,
        background: isDark ? "rgba(255,255,255,0.02)" : "rgba(248,250,252,0.8)",
      }}
    >
      <Typography variant="body2" color="text.disabled" sx={{ fontSize: "0.8rem" }}>
        No leads in this lane
      </Typography>
    </Box>
  );
}

function LaneSkeletonCards() {
  return (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      {[1, 2].map((i) => (
        <Skeleton
          key={i}
          variant="rounded"
          height={190}
          sx={{ borderRadius: "18px" }}
        />
      ))}
    </Stack>
  );
}

export default function BoardLane({ laneKey, cards, loading }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const config = LANE_CONFIG[laneKey];

  const headerBg = isDark ? config.bgDark : config.bgLight;
  const laneBorder = isDark ? config.borderDark : config.borderLight;
  const chipBg = isDark ? config.chipBgDark : config.chipBgLight;
  const chipText = isDark ? config.chipTextDark : config.chipTextLight;

  return (
    <Box
      sx={{
        borderRadius: "22px",
        border: `1px solid ${laneBorder}`,
        bgcolor: isDark ? "rgba(15,23,42,0.6)" : "rgba(255,255,255,0.75)",
        backdropFilter: "blur(10px)",
        boxShadow: isDark
          ? "inset 0 0 0 1px rgba(255,255,255,0.02)"
          : "inset 0 0 0 1px rgba(255,255,255,0.35)",
        p: 1.25,
        display: "flex",
        flexDirection: "column",
        minHeight: 320,
        ...(laneKey === "overdue" && {
          background: isDark ? "rgba(239,68,68,0.06)" : "rgba(239,68,68,0.04)",
          border: `1px solid ${isDark ? "rgba(248,113,113,0.18)" : "rgba(239,68,68,0.18)"}`,
        }),
      }}
    >
      {/* Lane header */}
      <Box
        sx={{
          borderRadius: "14px",
          background: headerBg,
          px: 2,
          py: 1.45,
          mb: 0.5,
          border: `1px solid ${isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.45)"}`,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                bgcolor: config.dotColor,
                flexShrink: 0,
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{ fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase" }}
            >
              {config.label}
            </Typography>
          </Stack>

          {/* Count pill */}
          <Box
            sx={{
              px: 1.25,
              py: 0.3,
              borderRadius: "20px",
              bgcolor: chipBg,
              color: chipText,
              fontSize: "0.72rem",
              fontWeight: 700,
              lineHeight: 1.6,
              minWidth: 24,
              textAlign: "center",
            }}
          >
            {loading ? "·" : cards.length}
          </Box>
        </Stack>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.4, display: "block", fontSize: "0.72rem", opacity: 0.8 }}
        >
          {config.sub}
        </Typography>
      </Box>

      {/* Cards list */}
      <Box sx={{ flex: 1, overflowY: "auto", maxHeight: { xs: "none", md: 680 }, pr: 0.5 }}>
        {loading ? (
          <LaneSkeletonCards />
        ) : cards.length === 0 ? (
          <LaneEmptyState isDark={isDark} />
        ) : (
          <Stack spacing={1.5} sx={{ mt: 1.5 }}>
            <AnimatePresence initial={false}>
              {cards.map((card, index) => (
                <motion.div
                  key={card.followup_id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8, scale: 0.97 }}
                  transition={{ delay: index * 0.04, duration: 0.22, ease: "easeOut" }}
                >
                  <BoardCard card={card} />
                </motion.div>
              ))}
            </AnimatePresence>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
